import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pedidoService, Pedido } from '../../services/pedido';

const PedidoTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Buscar todos os pedidos
  const { data: pedidos = [], isLoading, error } = useQuery({
    queryKey: ['pedidos'],
    queryFn: pedidoService.getAllTable,
  });

  // Filtrar pedidos baseado na busca
  const filteredPedidos = useMemo(() => {
    return pedidos.filter(pedido =>
      pedido.id?.toString().includes(search.toLowerCase()) ||
      pedido.nomeFornecedor?.toLowerCase().includes(search.toLowerCase()) ||
      pedido.dataPedidoString?.toLowerCase().includes(search.toLowerCase()) ||
      pedido.dataPrevistaEntregaString?.toLowerCase().includes(search.toLowerCase())
    );
  }, [pedidos, search]);

  // Paginação dos dados filtrados
  const paginatedPedidos = useMemo(() => {
    return filteredPedidos.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredPedidos, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, pedido: Pedido) => {
    setAnchorEl(event.currentTarget);
    setSelectedPedido(pedido);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPedido(null);
  };

  const handleEdit = () => {
    if (selectedPedido) {
      navigate(`/pedidos/editar/${selectedPedido.id}`);
      handleMenuClose();
    }
  };

  const handleView = () => {
    if (selectedPedido) {
      navigate(`/pedidos/visualizar/${selectedPedido.id}`);
      handleMenuClose();
    }
  };

  const handleNew = () => {
    navigate('/pedidos/novo');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar os pedidos.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Novo Pedido
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fornecedor</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data Pedido</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data Prevista</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPedidos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhum pedido encontrado para o filtro informado.' : 'Nenhum pedido cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPedidos.map((pedido) => (
                <TableRow key={pedido.id} hover>
                  <TableCell>{pedido.id}</TableCell>
                  <TableCell>{pedido.nomeFornecedor}</TableCell>
                  <TableCell>{pedido.dataPedidoString}</TableCell>
                  <TableCell>{pedido.dataPrevistaEntregaString}</TableCell>
                  <TableCell>{pedido.quantidade}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, pedido)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedPedido?.id === pedido.id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleView}>
                        <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
                        Visualizar
                      </MenuItem>
                      <MenuItem onClick={handleEdit}>
                        <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                        Editar
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredPedidos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default PedidoTable;
